import React, { useEffect, useState, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import ProLayout from '@ant-design/pro-layout';
import { Layout } from 'antd';

import { useGlobalLoader, GlobalLoader } from 'utils/global';
import { useTheme } from 'utils/global/ThemeContext';
import { authAPI, userAPI } from 'utils/portal-apis';
import { useUserStore, useBroadcastStore } from 'stores';

import { defaultSettings, getMenuRoutes, RightContent } from './common';
import styled from 'styled-components';

// Adjust the StyledProLayout styling if necessary for the top bar layout
const StyledProLayout = styled(ProLayout)`
  max-width: none !important;
  width: 100vw !important;
  padding-left: 0 !important;
  padding-right: 0 !important;

  &.ant-pro-layout,
  .ant-pro-layout-container,
  .ant-layout-content.ant-pro-layout-content,
  .ant-layout {
    height: 100%;
    overflow: hidden;
  }

  .ant-layout-content {
    height: 100%;
    overflow-y: auto;
  }

  .ant-pro-layout-container {
    height: 100vh;
  }

  .ant-pro-top-nav-header {
    background: #000000 !important;
    border-bottom: 1px solid var(--border-default, #3d3c41);
  }

  .ant-menu {
    background: transparent !important;
  }

  .ant-menu-item,
  .ant-menu-item a,
  .ant-menu-item span {
    font-family: 'Druk Text' !important;
    font-size: 16px !important;
    font-style: normal !important;
    font-weight: 500 !important;
    line-height: 120% !important;
    color: #ffffff !important;
  }

  .ant-menu-submenu-title,
  .ant-menu-submenu-title span {
    font-family: 'Druk Text' !important;
    font-size: 16px !important;
    font-style: normal !important;
    font-weight: 500 !important;
    line-height: 120% !important;
    color: #ffffff !important;
  }

  .ant-menu-item {
    border-bottom: none !important;
    padding-bottom: 0 !important;
  }

  .ant-menu-item-selected a {
    position: relative !important;

    &:after {
      content: '' !important;
      position: absolute !important;
      left: 0 !important;
      right: 0 !important;
      bottom: -5px !important;
      height: 3px !important;
      background: #ffffff !important;
    }
  }

  .ant-menu-item::after {
    display: none !important;
  }
`;

function PrivateRoute({ children, noHeader = false }) {
  const { loading } = useGlobalLoader();
  const [authenticated, setAuthenticated] = useState(() => authAPI.isLoggedIn() || authAPI.isRefreshable());
  // Initialize checkingToken to true if there's a token in URL (prevents flash of content)
  const [checkingToken, setCheckingToken] = useState(() => {
    return new URLSearchParams(window.location.search).has('token');
  });
  const isLive = useBroadcastStore(state => state.isLive);
  const user = useUserStore(state => state.user);
  const { theme } = useTheme();

  const location = useLocation();
  const tokenProcessedRef = useRef(false);

  useEffect(() => {
    let mounted = true;
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    if (token && !tokenProcessedRef.current) {
      // SSO flow: authenticate with URL token
      tokenProcessedRef.current = true;

      // Clean token from URL immediately (before async call) to prevent exposure
      urlParams.delete('token');
      const newSearch = urlParams.toString();
      window.history.replaceState(null, '', location.pathname + (newSearch ? `?${newSearch}` : ''));

      authAPI
        .getExternalToken(token)
        .then(result => {
          if (mounted) {
            setAuthenticated(!!(result && result._id));
          }
        })
        .catch(() => {
          if (mounted) setAuthenticated(false);
        })
        .finally(() => {
          if (mounted) setCheckingToken(false);
        });
    } else if (!token) {
      // Standard flow: check existing session with refresh token recovery
      const syncUser = async () => {
        try {
          const result = await userAPI.refreshUser();
          if (!mounted) return;

          if (result?._id) {
            setAuthenticated(true);
            return;
          }
        } catch (error) {
          console.warn('refreshUser failed:', error);

          // If the server rejected our access token (401) but we still have
          // a refresh token, try to get a new access token before giving up.
          if (mounted && authAPI.isRefreshable()) {
            try {
              const refreshed = await authAPI.refreshAuth();
              if (!mounted) return;
              if (refreshed && refreshed._id) {
                setAuthenticated(true);
                return;
              }
            } catch (refreshError) {
              console.warn('Recovery refresh also failed:', refreshError);
            }
          }
        }

        // Re-check local auth state. If the server rejected the refresh token,
        // refreshAuth() will have already cleared storage, so both checks
        // will return false and we'll redirect to login.
        if (mounted) {
          setAuthenticated(authAPI.isLoggedIn() || authAPI.isRefreshable());
        }
      };

      syncUser();
    }

    return () => {
      mounted = false;
    };
  }, []);

  // Show loading while validating SSO token
  if (checkingToken) {
    return <GlobalLoader loading={true}>{null}</GlobalLoader>;
  }

  if (authenticated && (authAPI.isLoggedIn() || authAPI.isRefreshable())) {
    if (user?.show_portal_onboarding) {
      localStorage.setItem('redirectURL', location.pathname);
      return <Navigate to="/auth/onboarding" />;
    }

    const menuRoutes = isLive ? getMenuRoutes(true) : getMenuRoutes(false);

    // Create a safe theme object with fallbacks for any missing properties
    const safeTheme = {
      ...theme,
      colors: {
        ...(theme?.colors || {}),
        primary: theme?.colors?.primary || '#6927f4',
        surface: {
          ...(theme?.colors?.surface || {}),
          level1: theme?.colors?.surface?.level1 || '#121212',
          level2: theme?.colors?.surface?.level2 || '#1A1A1A',
          level3: theme?.colors?.surface?.level3 || '#1f1f1f',
          focused: theme?.colors?.surface?.focused || '#3D3C41',
        },
      },
      logo: {
        ...(theme?.logo || {}),
        primary: theme?.logo?.primary || '/logo.svg',
        favicon: theme?.logo?.favicon || '/favicon.ico',
      },
      typography: {
        ...(theme?.typography || {}),
        fontFamily: {
          ...(theme?.typography?.fontFamily || {}),
          primary: theme?.typography?.fontFamily?.primary || '"Proxima Nova", Helvetica, Arial, sans-serif',
          secondary: theme?.typography?.fontFamily?.secondary || '"Maison Neue", Helvetica, Arial, sans-serif',
          mono:
            theme?.typography?.fontFamily?.mono || '"Maison Neue Mono", "Maison Neue", Helvetica, Arial, sans-serif',
        },
      },
      auth: {
        ...(theme?.auth || {}),
        ssoEnabled: theme?.auth?.ssoEnabled !== undefined ? theme?.auth?.ssoEnabled : false,
        ssoConfig: {
          ...(theme?.auth?.ssoConfig || {}),
          provider: theme?.auth?.ssoConfig?.provider || 'bleacherReport',
          displayName: theme?.auth?.ssoConfig?.displayName || 'Bleacher Report',
          loginButtonText: theme?.auth?.ssoConfig?.loginButtonText || 'Login with Bleacher Report',
          url: theme?.auth?.ssoConfig?.url || 'https://prd.api.bleacherreport.com/creator-login',
          clientId: theme?.auth?.ssoConfig?.clientId || '1ec9bfff-6731-49f3-a9be-2c7e6f0f790c',
          redirectUrl: theme?.auth?.ssoConfig?.redirectUrl || 'https://staging.app.hotmic.io/login/oauth/callback',
          popupWidth: theme?.auth?.ssoConfig?.popupWidth || 600,
          popupHeight: theme?.auth?.ssoConfig?.popupHeight || 700,
        },
      },
    };

    try {
      return (
        <GlobalLoader loading={loading}>
          <StyledProLayout
            {...Object.assign({}, defaultSettings, {
              logo: safeTheme?.logo?.primary || defaultSettings.logo,
              layout: 'top',
              headerRender: noHeader ? false : undefined,
            })}
            route={menuRoutes}
            rightContentRender={() => <RightContent isPublicPage={false} isLive={isLive} />}
            isPublicPage={false}
            isLive={isLive}
          >
            <Layout>
              <Layout.Content>{children}</Layout.Content>
            </Layout>
          </StyledProLayout>
        </GlobalLoader>
      );
    } catch (error) {
      // Fallback to a simple layout without ProLayout
      return (
        <GlobalLoader loading={loading}>
          <div style={{ padding: '20px' }}>
            <h1>Error in layout rendering</h1>
            <p>There was an error rendering the layout. Please check the console for details.</p>
            <div>{children}</div>
          </div>
        </GlobalLoader>
      );
    }
  } else {
    localStorage.setItem('redirectURL', location.pathname);
    return <Navigate to="/auth/login" />;
  }
}

export default PrivateRoute;
